// Where the shell's session comes from.
//
// STATE OF THE BACKEND, kept honest as it changes:
//   THERE IS AN INTERACTIVE LOGIN NOW (E12-t29). git-host/src/login.ts serves
//   POST /api/auth/{signup,signin,signout} above the /api fallthrough, and they
//   answer the SAME projection GET /api/session answers with, over the same
//   HttpOnly `dj_session` cookie. So the "Sign in" button has a backend at last:
//   src/api/auth.ts posts to it and src/pages/SignIn.tsx is the form. What this
//   module still does, unchanged, is the READ — the cookie is HttpOnly, so the
//   app can never inspect it and must ask the control plane who it is.
//
//   The cookie itself is `dj_session`, which platform/src/session-store.ts reads
//   and git-host/src/serve.ts resolveActor() turns into (actor, role) PER ORG on
//   every members-only request.
//
//   WHAT E08 ADDED: GET /api/session (git-host/src/catalog.ts) — the caller,
//   the orgs they are a member of WITH THEIR ROLE IN EACH, and the KBs the
//   registry holds for those orgs. It is a projection of E03's control-plane
//   tables and nothing else; it decides nothing and it invents nothing. Before
//   it, the app could find out whether a session was a member (probeMembership,
//   below) but not who it was or what it could do — and role-aware chrome
//   (Forge f1) is not something a client may guess.
//
//   401 from that endpoint is the unauthenticated answer, and this module
//   returns null rather than a fabricated identity: an org list is itself a
//   disclosure, so the shell shows the sign-in prompt and no chrome at all.
import type { KbSummary, OrgSummary, Role, SessionPayload } from './shell/AppShell';

/** The session cookie platform/src/session-store.ts reads. HttpOnly on the real
 *  deployment, so the app must never expect to read it from document.cookie. */
export const SESSION_COOKIE = 'dj_session';

/** git-host/src/catalog.ts GET /api/session — the members-only identity read. */
export const SESSION_ENDPOINT = '/api/session';

export type MembershipProbe =
  | { state: 'member' }
  | { state: 'unauthenticated' }
  | { state: 'forbidden'; guard: string | null };

/**
 * Ask the REAL backend whether the browser's `dj_session` is a member of this
 * KB, by calling the endpoint the contract suite pins.
 * GET /api/:org/:kb/refs — members only; the refusals carry `guard`.
 */
export async function probeMembership(
  org: string,
  kb: string,
  fetchImpl: typeof fetch = fetch,
): Promise<MembershipProbe> {
  const response = await fetchImpl(`/api/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/refs`, {
    credentials: 'same-origin',
  });
  if (response.status === 401) return { state: 'unauthenticated' };
  if (response.status === 403) {
    const body = (await response.json().catch(() => ({}))) as { guard?: string };
    return { state: 'forbidden', guard: body.guard ?? null };
  }
  if (response.ok) return { state: 'member' };
  return { state: 'forbidden', guard: null };
}

/** The wire shape of GET /api/session, exactly as git-host answers it. */
interface SessionResponse {
  user?: { id?: string; email?: string };
  orgs?: Array<{ id?: string; name?: string; role?: string }>;
  kbs?: Array<{ id?: string; name?: string; org?: string }>;
}

const ROLES: readonly Role[] = ['admin', 'maintainer', 'viewer'];
const asRole = (value: unknown): Role | null =>
  ROLES.includes(value as Role) ? (value as Role) : null;

/**
 * ONE READER of the projection, for the TWO surfaces that answer it.
 *
 * git-host/src/catalog.ts::sessionProjection is what GET /api/session answers AND
 * what POST /api/auth/{signup,signin} answer — extracted there so the two can
 * never disagree about who is signed in. This function is the client-side half of
 * that same decision: src/api/auth.ts calls it on a fresh login, `loadSession`
 * below calls it on a cold page load, and there is no second parser to drift.
 *
 * `activeOrg`/`activeKb` are deliberately left null: the URL is the source of
 * truth for what the reader is looking at (App.tsx), and this module's job is only
 * to say what the control plane holds. A role that is not one of guards.ts's three
 * is dropped rather than trusted.
 */
export function parseSessionPayload(body: unknown): SessionPayload | null {
  const found = body as SessionResponse | null;
  if (!found || !found.user || typeof found.user.id !== 'string') return null;

  const orgs: OrgSummary[] = [];
  for (const org of found.orgs ?? []) {
    const role = asRole(org?.role);
    if (!org || typeof org.name !== 'string' || role === null) continue;
    orgs.push({ id: typeof org.id === 'string' ? org.id : org.name, name: org.name, role });
  }
  const kbs: KbSummary[] = [];
  for (const kb of found.kbs ?? []) {
    if (!kb || typeof kb.name !== 'string' || typeof kb.org !== 'string') continue;
    kbs.push({ id: typeof kb.id === 'string' ? kb.id : `${kb.org}/${kb.name}`, name: kb.name, org: kb.org });
  }

  return {
    user: { id: found.user.id, email: typeof found.user.email === 'string' ? found.user.email : '' },
    orgs,
    kbs,
    activeOrg: null,
    activeKb: null,
  };
}

/** WHO IS ASKING — or null when nobody is. */
export async function loadSession(fetchImpl: typeof fetch = fetch): Promise<SessionPayload | null> {
  let response: Response;
  try {
    response = await fetchImpl(SESSION_ENDPOINT, { credentials: 'same-origin' });
  } catch {
    // The backend being unreachable is not a session; it is certainly not one
    // this app may invent a membership for.
    return null;
  }
  if (!response.ok) return null;
  return parseSessionPayload(await response.json().catch(() => null));
}

/** The globals a server-rendered bootstrap would use, if one is ever added. */
declare global {
  interface Window {
    __DJ_SESSION__?: SessionPayload;
  }
}

/** A session inlined by the server, or null. Never a fabricated one. */
export function readBootstrap(): SessionPayload | null {
  return typeof window === 'undefined' ? null : (window.__DJ_SESSION__ ?? null);
}
